import {
  Group,
  GroupCustomSegment,
  GroupCustomSegmentNode,
  GroupCustomSegmentTypes,
  GroupHiddenCustomSegment,
  GroupRole,
} from '@libs/src/models/group.model';
import { UploadCSVUniqueColumn } from '@libs/src/utilities/csv.utilities';
import {
  AttributeMapping,
  ColumnTransformations,
  ColumnValue,
  SegmentOption,
  SegmentValueOptions,
  UniqueColumn,
  ValueTransformations,
} from '@main-client/src/app/invite-csv/invite-csv-view/invite-csv-view.interface';

import find from 'lodash-es/find';
import isEmpty from 'lodash-es/isEmpty';

export const accountFields = 'account_fields';
export const groupFields = 'group_fields';
export const role = 'role';
export const accountFieldOptions = [
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.firstName:First name`,
    value: 'first_name',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.lastName:Last name`,
    value: 'last_name',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.email:Email`,
    value: 'email',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.externalId:External ID`,
    value: 'external_id',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.jobTitle:Job title`,
    value: 'experience.job_title',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.organizationName:Organization name`,
    value: 'experience.organization_name',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.locale:Locale`,
    value: 'locale_config.locale_selected',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.employeeStatus:Employee Status`,
    value: 'status',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.classOf:Class of`,
    value: 'education.class_of',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.faculty:Faculty`,
    value: 'education.faculty',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.program:Program`,
    value: 'education.program',
  },
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.school:School`,
    value: 'education.school',
  },
];
export const accountFieldsList = [
  'first_name',
  'last_name',
  'email',
  'external_id',
  'experience.job_title',
  'experience.organization_name',
  'locale_config.locale_selected',
  'status',
  'education.class_of',
  'education.faculty',
  'education.program',
  'education.school',
];
export const groupFieldOptions = [
  {
    label: $localize`:@@attributeMappingDrawer.attributeTable.role:Role`,
    value: 'role',
  },
];
export interface VisibleUniqueColumn extends UniqueColumn {
  hasBeenReviewed: boolean;
  mappedValuesCount?: number;
}
export interface AttributeSelection {
  attributeId?: string;
  custom_segment?: string;
  translation: string;
  type?: string;
  values: ColumnValue[];
}

export interface GroupMappingOptions {
  account_fields: MappingOption[];
  custom_segments?: MappingOption[];
  group_fields: MappingOption[];
  hidden_custom_segments?: MappingOption[];
}

export interface MappingOption {
  label: string;
  value: string;
}

export interface SelectionType {
  section: string;
  selection: MappingOption;
}

export function getDestinationPath(group: Group, uniqueColumn: UniqueColumn) {
  if (role === uniqueColumn.translation) {
    return {
      type: 'groups.role',
    };
  }
  if (accountFieldsList.includes(uniqueColumn.translation)) {
    return {
      type: uniqueColumn.translation,
    };
  }
  const translation = findCustomSegment(group, uniqueColumn.translation);
  const hiddenCustomSegmentIds = group.hidden_custom_segments?.map(
    (customSegment) => customSegment._id,
  );
  return {
    customSegmentTranslation: translation?._id,
    type: hiddenCustomSegmentIds?.includes(translation._id)
      ? 'groups.hidden_segment_selections.node'
      : 'groups.segment_selections.node',
  };
}

export function getColumnTranslation(
  attributeMappings: AttributeMapping[],
  group: Group,
  uniqueColumn: UploadCSVUniqueColumn,
) {
  const columnAttributeMapping = getColumnAttributeMapping(
    attributeMappings,
    uniqueColumn,
  );
  const hiddenSegmentOptions = initSegmentOptions(
    group.hidden_custom_segments,
    true,
  );
  const segmentOptions = initSegmentOptions(group.custom_segments);
  const segmentTranslation = getSegmentTranslation(
    hiddenSegmentOptions,
    segmentOptions,
    uniqueColumn,
    columnAttributeMapping,
  );
  const segmentValueOptions = initSegmentValueOptions(group.roles, [
    ...group.custom_segments,
    ...group.hidden_custom_segments,
  ]);
  const columnTranslationBase = {
    multiple:
      'multi' ===
      findCustomSegment(group, segmentTranslation?.id)?.selection_type,
    skip: false,
    translation: segmentTranslation?.id || '',
  };
  if (columnAttributeMapping) {
    const valueTransformations =
      columnAttributeMapping.value_transformations.filter(
        (columnTransformation) =>
          uniqueColumn.columnValues.includes(columnTransformation.source_value),
      );
    return {
      ...columnTranslationBase,
      attributeId: columnAttributeMapping._id,
      name: columnAttributeMapping.source_path[0],
      values: getAttributeMappingsValuesTranslation(
        valueTransformations,
        segmentTranslation,
        segmentValueOptions,
        uniqueColumn.columnValues,
      ),
    };
  }
  return {
    ...columnTranslationBase,
    name: uniqueColumn.columnName,
    values: getColumnValuesTranslation(
      uniqueColumn.columnValues,
      segmentTranslation,
      segmentValueOptions,
    ),
  };
}

export function initSegmentOptions(
  customSegments: GroupCustomSegmentTypes[] = [],
  isPrivate?: boolean,
) {
  return [
    ...(!isPrivate ? [{ id: role, value: role }] : []),
    ...(customSegments?.map((customSegment: GroupCustomSegmentTypes) => ({
      id: customSegment._id,
      value: customSegment.title,
    })) ?? []),
  ];
}

export function initSegmentValueOptions(
  roles: GroupRole[] = [],
  customSegments: (GroupCustomSegment | GroupHiddenCustomSegment)[] = [],
) {
  return {
    [role]: roles.map((groupRole: GroupRole) => ({
      id: groupRole._id,
      value: groupRole.name,
    })),
    ...customSegments
      .filter(
        (customSegment: GroupCustomSegment | GroupHiddenCustomSegment) =>
          !isEmpty(customSegment.nodes),
      )
      .reduce(
        (
          result: { [customSegmentId: string]: SegmentOption },
          customSegment: GroupCustomSegment | GroupHiddenCustomSegment,
        ) => ({
          ...result,
          [customSegment._id]: customSegment?.nodes?.map(
            (node: GroupCustomSegmentNode) => ({
              id: node._id,
              value: node.name,
            }),
          ),
        }),
        {},
      ),
  };
}

function getColumnAttributeMapping(
  attributeMappings: AttributeMapping[],
  uniqueColumn: UniqueColumn | UploadCSVUniqueColumn,
) {
  const columnAttributeMapping = attributeMappings.find((attributeMapping) => {
    if ((uniqueColumn as UniqueColumn).attributeId) {
      return (
        (uniqueColumn as UniqueColumn).attributeId === attributeMapping._id
      );
    }
    return (
      (uniqueColumn as UploadCSVUniqueColumn).columnName ===
      attributeMapping.source_path[0]
    );
  });
  if ('groups.role' === columnAttributeMapping?.destination_path) {
    columnAttributeMapping.custom_segment = role;
  }
  return columnAttributeMapping;
}

export function findCustomSegment(
  group: Group,
  customSegmentId: string,
): GroupCustomSegment {
  const groupCustomSegments = [
    ...(group.custom_segments || []),
    ...(group.hidden_custom_segments || []),
  ];
  return find(groupCustomSegments, {
    _id: customSegmentId,
  });
}

function getSegmentTranslation(
  hiddenSegmentOptions: SegmentOption[],
  segmentOptions: SegmentOption[],
  uniqueColumn: UploadCSVUniqueColumn,
  columnAttributeMapping?: AttributeMapping,
): SegmentOption {
  const allSegmentOptions = [...segmentOptions, ...hiddenSegmentOptions];
  const isColumnForRole =
    (columnAttributeMapping &&
      'groups.role' === columnAttributeMapping.destination_path) ||
    (!columnAttributeMapping && uniqueColumn.columnName === role);
  if (!isColumnForRole) {
    return allSegmentOptions
      .filter(({ id }) => id !== role)
      .find((option) => option.id === columnAttributeMapping.custom_segment);
  }
  return allSegmentOptions.find(
    (option) => option.id === columnAttributeMapping.custom_segment,
  );
}

export function getAttributeMappingsValuesTranslation(
  columnTransformations: ColumnTransformations[],
  columnNameTranslation: SegmentOption,
  segmentValueOptions: SegmentValueOptions,
  uniqueColumnValues: string[] = [],
) {
  const attributeMappingColumnOptions = columnTransformations.map(
    (columnTransformation: ColumnTransformations) => {
      const translatedSegmentOptions =
        segmentValueOptions[columnNameTranslation?.id] || [];
      const translatedNode = translatedSegmentOptions.filter((option) =>
        columnTransformation.destination_value.includes(option.id),
      );
      return {
        name: columnTransformation.source_value,
        translation: translatedNode.map((node) => node.id),
        translationValues: translatedNode.map((node) => node.value),
      };
    },
  );
  if (!uniqueColumnValues.length) {
    return attributeMappingColumnOptions;
  }
  const uniqueColumnsOptions = uniqueColumnValues
    .filter(
      (value) =>
        !attributeMappingColumnOptions.some(
          (mapping) => mapping.name === value,
        ),
    )
    .map((filterValue) => {
      const existingSegment = find(
        segmentValueOptions[columnNameTranslation?.id],
        (option) => option.value === filterValue,
      );
      return {
        name: filterValue,
        translation: existingSegment ? [existingSegment.id] : [],
        translationValues: existingSegment ? [existingSegment.value] : [],
      };
    });
  return attributeMappingColumnOptions.concat(uniqueColumnsOptions);
}

function getColumnValuesTranslation(
  columnValues: string[],
  columnNameTranslation: SegmentOption,
  segmentValueOptions: SegmentValueOptions,
) {
  return columnValues.map((columnValue: string) => {
    const translatedNode = find(
      segmentValueOptions[columnNameTranslation?.id],
      (option) => option.value === columnValue,
    );
    return {
      name: columnValue,
      translation: translatedNode?.id ? [translatedNode?.id] : [],
      translationValues: [],
    };
  });
}

export function createNewUniqueColumn(
  column: UploadCSVUniqueColumn,
  isAccountField: MappingOption['value'],
): UniqueColumn {
  return {
    multiple: false,
    name: column.columnName,
    skip: false,
    translation: isAccountField,
    values: column.columnValues.map((value) => ({
      name: value,
      translation: [],
    })),
  };
}

export function buildValueTransformations(
  columnValues: ColumnValue[],
  translation: string,
): ValueTransformations[] {
  if (accountFieldsList.includes(translation)) {
    return [];
  }
  return columnValues.map((value) => ({
    destination_value: value.translation,
    source_value: value.name,
  }));
}
